import { type Calendar } from 'fullcalendar';

/**
 * Custom calendar toolbar — a centered, segmented tab bar rendered above the
 * calendar (outside FullCalendar's own `headerToolbar`). FC v7's header toolbar
 * is a single row with only three zones, so the legacy two-row layout (centered
 * tabs on top, split nav arrows below) cannot be reproduced with FC options
 * alone. FC keeps the nav row (prev/next + title); this owns the view tabs and
 * the print/email/subscribe actions, mirroring the legacy `fullcalendar-toolbar`.
 *
 * Accessibility: the view switcher is a `role="group"` of toggle buttons whose
 * active state is reflected via `aria-pressed`, kept in sync with the live view
 * (including arrow navigation and deep links) through `syncActive`.
 */

interface ViewTab {
    label: string;
    view: string;
    matches: (viewType: string) => boolean;
}

const VIEW_TABS: ViewTab[] = [
    { label: 'Month', view: 'dayGridMonth', matches: (t) => t === 'dayGridMonth' },
    { label: 'Week', view: 'timeGridWeek', matches: (t) => t === 'timeGridWeek' },
    { label: 'Day', view: 'timeGridDay', matches: (t) => t === 'timeGridDay' },
    { label: 'List', view: 'listMonth', matches: (t) => t.startsWith('list') },
];

// Meal calendars force a list-only display (the legacy page hides the
// Calendar/List toggle but keeps Month/Week/Day to change the range). Mirror
// that by mapping the range tabs to FullCalendar's list views instead of the
// grid views, with no separate "List" tab.
const MEAL_VIEW_TABS: ViewTab[] = [
    { label: 'Month', view: 'listMonth', matches: (t) => t === 'listMonth' },
    { label: 'Week', view: 'listWeek', matches: (t) => t === 'listWeek' },
    { label: 'Day', view: 'listDay', matches: (t) => t === 'listDay' },
];

export interface ToolbarConfig {
    isMealCalendar: boolean;
    getCalendar: () => Calendar | null;
    onPrint: (email: boolean) => void;
    onSubscribe: () => void;
}

export interface CalendarToolbar {
    element: HTMLElement;
    syncActive: (viewType: string) => void;
}

const createTabButton = (label: string): HTMLButtonElement => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'pcal-toolbar__tab';
    button.textContent = label;
    button.setAttribute('aria-pressed', 'false');
    return button;
};

const createActionButton = (label: string, onClick: () => void): HTMLButtonElement => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'pcal-toolbar__action';
    button.textContent = label;
    button.addEventListener('click', onClick);
    return button;
};

const buildViewGroup = (
    getCalendar: () => Calendar | null,
    tabSource: ViewTab[],
): { group: HTMLElement; tabs: Array<{ tab: ViewTab; button: HTMLButtonElement }> } => {
    const group = document.createElement('div');
    group.className = 'pcal-toolbar__views';
    group.setAttribute('role', 'group');
    group.setAttribute('aria-label', 'Calendar view');

    const tabs = tabSource.map((tab) => {
        const button = createTabButton(tab.label);
        button.addEventListener('click', () => {
            getCalendar()?.changeView(tab.view);
        });
        group.appendChild(button);
        return { tab, button };
    });

    return { group, tabs };
};

const buildActionGroup = (config: ToolbarConfig): HTMLElement => {
    const group = document.createElement('div');
    group.className = 'pcal-toolbar__actions';
    group.appendChild(createActionButton('Print', () => config.onPrint(false)));
    group.appendChild(createActionButton('Email', () => config.onPrint(true)));
    group.appendChild(createActionButton('Subscribe', config.onSubscribe));
    return group;
};

export const createToolbar = (config: ToolbarConfig): CalendarToolbar => {
    const element = document.createElement('div');
    element.className = 'pcal-toolbar';
    element.setAttribute('role', 'group');
    element.setAttribute('aria-label', 'Calendar views and actions');

    // Meal calendars keep Month/Week/Day (mapped to list-range views) but drop
    // the grid views and the standalone "List" tab; everything else gets the
    // full Month/Week/Day/List switcher.
    const tabSource = config.isMealCalendar ? MEAL_VIEW_TABS : VIEW_TABS;
    const { group, tabs } = buildViewGroup(config.getCalendar, tabSource);
    const viewTabs = tabs;
    element.appendChild(group);

    element.appendChild(buildActionGroup(config));

    const syncActive = (viewType: string): void => {
        viewTabs.forEach(({ tab, button }) => {
            const active = tab.matches(viewType);
            button.setAttribute('aria-pressed', String(active));
            button.classList.toggle('is-active', active);
        });
    };

    return { element, syncActive };
};
